import {
  ASTNode,
  BinaryOperator,
  BinaryOperatorType,
  FunctionOperator,
  UnaryOperator,
  UnaryOperatorType,
  Value,
} from 'notebook/tables/excess/AST';
import Parser from 'notebook/tables/excess/Parser';
import tokenizer from 'notebook/tables/excess/Tokenizer';
import {FormulaParseError, FormulaTokenizeError} from 'notebook/tables/excess/Util';

import {
  AnalysisFormulaParsingError,
  BOOLEAN_LITERAL_TYPE,
  COLUMN_REFERENCE_TYPE,
  COLUMN_REFERENCE_PREFIX,
  FormulaAst,
  NUMBER_LITERAL_TYPE,
  OPERATION_TYPE,
  STRING_LITERAL_TYPE,
  COLUMN_REFERENCE_REGEX,
} from './Constants';
import {analysisFunctions} from './functionUtils';

type FormulaOperation = Extract<FormulaAst, {type: typeof OPERATION_TYPE}>['operation'];

const BINARY_OPERATOR_TO_OPERATION: {[k in BinaryOperatorType]: FormulaOperation} = {
  [BinaryOperatorType.ADDITION]: 'ADD',
  [BinaryOperatorType.SUBTRACTION]: 'SUBTRACT',
  [BinaryOperatorType.MULTIPLICATION]: 'MULTIPLY',
  [BinaryOperatorType.DIVISION]: 'DIVIDE',
  [BinaryOperatorType.EXPONENTIAL]: 'POWER',
  [BinaryOperatorType.EQUALS]: 'EQUALS',
  [BinaryOperatorType.NOT_EQUALS]: 'NOT_EQUALS',
  [BinaryOperatorType.GREATER_THAN]: 'GREATER_THAN',
  [BinaryOperatorType.GREATER_THAN_OR_EQUAL]: 'GREATER_THAN_OR_EQUALS',
  [BinaryOperatorType.LESS_THAN]: 'LESS_THAN',
  [BinaryOperatorType.LESS_THAN_OR_EQUAL]: 'LESS_THAN_OR_EQUALS',
};

const FUNCTION_NAME_TO_ANALYSIS_OPERATION_NAME: {[k: string]: FormulaOperation} = {
  ABS: 'ABSOLUTE_VALUE',
  FACT: 'FACTORIAL',
  LN: 'NATURAL_LOG',
  LOG: 'LOG_10',
  MOD: 'MODULUS',
  STDEV: 'STANDARD_DEVIATION',
  VAR: 'VARIANCE',
  MED: 'MEDIAN',
  GEOMEAN: 'GEOMETRIC_MEAN',
  SE: 'STANDARD_ERROR',
  COUNTBLANKS: 'COUNT_BLANKS',
  ISBLANK: 'IS_BLANK',
  MID: 'SUBSTRING',
  LEN: 'LENGTH',
  TEXTJOIN: 'TEXT_JOIN',
  DATEBUCKET: 'DATE_TRUNC_INTERVAL',
  DATECONVERT: 'DATE_CONVERT',
  DATEDIF: 'DATE_DIFF',
  DATETRUNC: 'DATE_TRUNC',
  NULL: 'NULL_VALUE',
  VLOOKUP: 'VLOOKUP',
};

function unaryOperatorToJson(unaryNode: UnaryOperator): FormulaAst {
  switch (unaryNode.type) {
    case UnaryOperatorType.NEGATION:
      return {
        type: OPERATION_TYPE,
        operation: 'MULTIPLY',
        arguments: [{type: NUMBER_LITERAL_TYPE, value: -1}, astToRequestJson(unaryNode.child)],
      };
    case UnaryOperatorType.PLUS:
      return astToRequestJson(unaryNode.child);
    default:
      throw new AnalysisFormulaParsingError(`Unexpected operator: ${unaryNode.type}`);
  }
}

function validateFunction(functionNode: FunctionOperator): void {
  // Validate that function:
  //   1. Has a valid name
  //   2. Has the correct number of arguments
  const analysisFunction = analysisFunctions.find(
    (analysisFunction) => analysisFunction.name === functionNode.type
  );
  if (analysisFunction == null) {
    throw new AnalysisFormulaParsingError(`Unexpected operator: ${functionNode.type}.`);
  }

  const expectedMinNumArguments = analysisFunction.params.length;
  const expectedMaxNumArguments =
    analysisFunction.repeatableLastParamDescription != null ? Infinity : expectedMinNumArguments;
  const numProvidedArguments = functionNode.childNodes().length;
  if (numProvidedArguments < expectedMinNumArguments || numProvidedArguments > expectedMaxNumArguments) {
    throw new AnalysisFormulaParsingError(`Wrong number of arguments to ${analysisFunction.name}.`);
  }
}

function functionNodeToJson(functionNode: FunctionOperator): FormulaAst {
  validateFunction(functionNode);
  const argumentJsons = functionNode.childNodes().map((childNode) => astToRequestJson(childNode));
  // We don't have corresponding SQRT/CONCATENATE functions in the backend, but we can transform them to
  // other functions that have identical functionality
  if (functionNode.type === 'SQRT') {
    return {
      type: OPERATION_TYPE,
      operation: 'POWER',
      arguments: argumentJsons.concat([{type: NUMBER_LITERAL_TYPE, value: 0.5}]),
    };
  } else if (functionNode.type === 'CONCATENATE') {
    return {
      type: OPERATION_TYPE,
      operation: 'TEXT_JOIN',
      arguments: [{type: STRING_LITERAL_TYPE, value: ''} as FormulaAst].concat(argumentJsons),
    };
  } else if (FUNCTION_NAME_TO_ANALYSIS_OPERATION_NAME[functionNode.type] != null) {
    return {
      type: OPERATION_TYPE,
      operation: FUNCTION_NAME_TO_ANALYSIS_OPERATION_NAME[functionNode.type],
      arguments: argumentJsons,
    };
  } else {
    return {
      type: OPERATION_TYPE,
      operation: functionNode.type as FormulaOperation,
      arguments: argumentJsons,
    };
  }
}

function valueNodeToJson(valueNode: Value): FormulaAst {
  switch (valueNode.typedValue.type) {
    case 'NUMBER':
      return {type: NUMBER_LITERAL_TYPE, value: valueNode.typedValue.rawValue};
    case 'BOOLEAN':
      return {type: BOOLEAN_LITERAL_TYPE, value: valueNode.typedValue.booleanValue};
    case 'STRING':
      return valueNode.typedValue.rawValue.startsWith(COLUMN_REFERENCE_PREFIX)
        ? {
            type: COLUMN_REFERENCE_TYPE,
            value: valueNode.typedValue.rawValue.slice(COLUMN_REFERENCE_PREFIX.length),
          }
        : {type: STRING_LITERAL_TYPE, value: valueNode.typedValue.rawValue};
    default:
      throw new AnalysisFormulaParsingError('Invalid formula.');
  }
}

function astToRequestJson(astNode: ASTNode): FormulaAst {
  if (astNode instanceof BinaryOperator) {
    return {
      type: OPERATION_TYPE,
      operation: BINARY_OPERATOR_TO_OPERATION[astNode.type],
      arguments: astNode.childNodes().map((childNode) => astToRequestJson(childNode)),
    };
  } else if (astNode instanceof UnaryOperator) {
    return unaryOperatorToJson(astNode);
  } else if (astNode instanceof FunctionOperator) {
    return functionNodeToJson(astNode);
  } else if (astNode instanceof Value) {
    return valueNodeToJson(astNode);
  } else {
    throw new AnalysisFormulaParsingError('Invalid formula.');
  }
}

function parseAnalysisFormula(formulaText: string): ASTNode {
  try {
    // Reimplement parseWithoutCellRefs because we need a different error message when cell refs are present
    const tokens = tokenizer.tokenize(formulaText);
    if (tokens.some((token) => token.isCellRef())) {
      throw new AnalysisFormulaParsingError('Cannot reference cells in analysis formulas.');
    }

    const parsedToken = Parser._reduce(tokens);
    if (!parsedToken.node.isSingular()) {
      throw new AnalysisFormulaParsingError('Invalid formula.');
    }
    return parsedToken.node;
  } catch (e) {
    if (e instanceof FormulaTokenizeError || e instanceof FormulaParseError) {
      throw new AnalysisFormulaParsingError(e.message);
    }
    throw e;
  }
}

/**
 * Preprocess column references so that they're compatible with the notebook parser.
 *
 * Concatenates a space to the end of each of column reference. We need to do this because the parser treats
 * double quotes ("") as a single escaped quote within a string, so without a space between them two
 * consecutive column references (e.g. "$c$abc""$c$def") are parsed as a single string. We can safely add a
 * space since the parser ignores whitespaces.
 */
function preprocessColumnReferences(formulaText: string): string {
  return formulaText.replace(COLUMN_REFERENCE_REGEX, '$& ');
}

export default function formulaTextToFormulaAstJson(formulaText: string): FormulaAst {
  const preprocessedFormulaText = preprocessColumnReferences(formulaText);
  const ast = parseAnalysisFormula(preprocessedFormulaText);
  return astToRequestJson(ast);
}
